'use client'

import {
	type MouseEvent,
	useState,
	useEffect,
	useCallback,
	useMemo,
} from 'react'

import { useQueryClient } from '@tanstack/react-query'
import { Search } from 'lucide-react'

import { Campaign } from '@/core/api/entities/campaign/campaign'
import { Group } from '@/core/api/entities/group'
import { Playlist } from '@/core/api/entities/playlist/playlist'
import { Schedule } from '@/core/api/entities/schedule'
import { Song } from '@/core/api/entities/song/song'
import {
	MAX_PLAYLISTS_PER_GROUP_OR_ZONE,
	MAX_SCHEDULES_PER_GROUP_OR_ZONE,
} from '@/core/constants/assignmentLimits'
import { useAccountConfig } from '@/core/hooks/useAccountConfig'
import { usePaginateCampaigns } from '@/core/hooks/useCampaign'
import {
	useGetGroupSchedules,
	useUpdateGroupSchedules,
	useUpdateGroupCampaigns,
} from '@/core/hooks/useGroup'
import { usePlaylists, usePlaylistSample } from '@/core/hooks/usePlaylist'
import { useSchedules } from '@/core/hooks/useSchedule'
import { useDebounce } from '@/core/hooks/utils/useDebounce'
import { useTranslation } from '@/core/i18n/useTranslation'
import { Button } from '@/shared/components/atoms/Button'
import { Checkbox } from '@/shared/components/atoms/Checkbox'
import { Input } from '@/shared/components/atoms/Input'
import { ScrollArea } from '@/shared/components/atoms/Scroll'
import { Separator } from '@/shared/components/atoms/Separator'
import {
	TabsBadge,
	Tabs,
	TabsContent,
	TabsList,
	TabsTrigger,
} from '@/shared/components/atoms/Tabs'
import {
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/shared/components/molecules/Dialog/dialog'
import { ModalPaginator } from '@/shared/components/molecules/ModalPaginator/ModalPaginator'
import { PlaylistRow } from '@/shared/components/molecules/PlaylistRow/PlaylistRow'
import { ScheduleRow } from '@/shared/components/molecules/ScheduleRow/ScheduleRow'
import Player from '@/shared/components/organisms/Player/Player'
import { useNotificationContext } from '@/shared/context/NotificationProvider'

const DEFAULT_PAGE = 1
const DEFAULT_PAGE_SIZE = 20

type ContentTab = 'playlists' | 'schedules' | 'campaigns'

interface AddContentToGroupModalProps {
	visible: boolean
	onClose: () => void
	group: Group | null
	initialTab?: ContentTab
	mode?: 'edit' | 'create' | 'draft'
	onConfirmCreate?: (
		scheduleIds: number[],
		campaignIds: number[],
		playlistIds: number[]
	) => void
	onConfirmDraft?: (
		schedules: Schedule[],
		campaigns: Campaign[],
		playlists: Playlist[]
	) => void
	isCreating?: boolean
	showPlaylistsTab?: boolean
}

export function AddContentToGroupModal({
	visible,
	onClose,
	group,
	initialTab = 'schedules',
	mode = 'edit',
	onConfirmCreate,
	onConfirmDraft,
	isCreating = false,
	showPlaylistsTab = false,
}: AddContentToGroupModalProps) {
	const { t } = useTranslation()
	const isCreateMode = mode === 'create'
	const isDraftMode = mode === 'draft'
	const queryClient = useQueryClient()
	const { showNotification } = useNotificationContext()
	const [activeTab, setActiveTab] = useState<ContentTab>(initialTab)
	const [searchQuery, setSearchQuery] = useState('')
	const [selectedScheduleIds, setSelectedScheduleIds] = useState<number[]>([])
	const [selectedCampaignIds, setSelectedCampaignIds] = useState<number[]>([])
	const [selectedPlaylistIds, setSelectedPlaylistIds] = useState<number[]>([])
	const [isAssigning, setIsAssigning] = useState(false)

	// Store selected items as full objects to preserve them across pagination
	const [selectedSchedules, setSelectedSchedules] = useState<Schedule[]>([])
	const [selectedCampaigns, setSelectedCampaigns] = useState<Campaign[]>([])
	const [selectedPlaylists, setSelectedPlaylists] = useState<Playlist[]>([])

	const [schedulesPage, setSchedulesPage] = useState(DEFAULT_PAGE)
	const schedulesPageSize = DEFAULT_PAGE_SIZE

	// Pagination state for campaigns
	const [campaignsPage, setCampaignsPage] = useState(DEFAULT_PAGE)
	const campaignsPageSize = DEFAULT_PAGE_SIZE

	// Pagination state for playlists
	const [playlistsPage, setPlaylistsPage] = useState(DEFAULT_PAGE)
	const playlistsPageSize = DEFAULT_PAGE_SIZE

	// Audio preview state
	const [playingScheduleId, setPlayingScheduleId] = useState<number | null>(
		null
	)
	const [playingPlaylist, setPlayingPlaylist] = useState<Playlist | null>(null)
	const [currentSong, setCurrentSong] = useState<Song | null>(null)
	const [playlistIdForSample, setPlaylistIdForSample] = useState<number | null>(
		null
	)

	// Debounce search query for server-side search
	const debouncedSearchQuery = useDebounce(searchQuery, 500)

	// Reset page to 1 when search query changes
	useEffect(() => {
		setSchedulesPage(DEFAULT_PAGE)
		setCampaignsPage(DEFAULT_PAGE)
		setPlaylistsPage(DEFAULT_PAGE)
	}, [debouncedSearchQuery])

	// Reset activeTab when initialTab changes
	useEffect(() => {
		setActiveTab(initialTab)
	}, [initialTab])

	const schedulesInclude = 'timeslots.timeslot_contents.playlists'

	// Fetch private schedules (first page only - most users have few private schedules)
	const {
		data: privateSchedulesResponse,
		isLoading: isLoadingPrivateSchedules,
	} = useSchedules({
		config: useAccountConfig({
			params: {
				per_page: schedulesPageSize,
				page: schedulesPage,
				query: debouncedSearchQuery || undefined,
				include: schedulesInclude,
				public: false,
			},
		}),
		options: {
			enabled: visible && activeTab === 'schedules',
		},
	})

	const privateSchedules = privateSchedulesResponse?.data || []

	// Fetch public schedules with pagination
	const { data: publicSchedulesResponse, isLoading: isLoadingPublicSchedules } =
		useSchedules({
			config: useAccountConfig({
				params: {
					per_page: schedulesPageSize,
					page: schedulesPage,
					query: debouncedSearchQuery || undefined,
					include: schedulesInclude,
					public: true,
				},
			}),
			options: {
				enabled: visible && activeTab === 'schedules',
			},
		})

	const publicPaginationMeta = publicSchedulesResponse?.meta?.pagination
	const publicTotalPages = publicPaginationMeta?.total_pages ?? 1

	// Calculate combined pagination
	// Page 1 shows private + public, subsequent pages show only public
	const schedulesPaginationMeta = {
		total_pages: publicTotalPages,
	}

	const assignedScheduleIds = useMemo(() => {
		return new Set(selectedSchedules.map((schedule) => schedule.id))
	}, [selectedSchedules])

	const sortSchedulesByTitle = useCallback((a: Schedule, b: Schedule) => {
		return (a.title || '').localeCompare(b.title || '', undefined, {
			sensitivity: 'base',
		})
	}, [])

	// Assigned schedules: shown first, private before public, then alphabetical
	const assignedSchedules = useMemo(() => {
		const uniqueSchedules = Array.from(
			new Map(
				selectedSchedules.map((schedule) => [schedule.id, schedule])
			).values()
		)

		return uniqueSchedules.sort((a: Schedule, b: Schedule) => {
			const aPublic = a.public ? 1 : 0
			const bPublic = b.public ? 1 : 0
			if (aPublic !== bPublic) return aPublic - bPublic

			return sortSchedulesByTitle(a, b)
		})
	}, [selectedSchedules, sortSchedulesByTitle])

	// Unassigned private schedules (shown on page 1 only, after assigned)
	const unassignedPrivateSchedules = useMemo(() => {
		if (schedulesPage !== 1) return []
		return privateSchedules
			.filter((schedule: Schedule) => !assignedScheduleIds.has(schedule.id))
			.sort(sortSchedulesByTitle)
	}, [
		privateSchedules,
		assignedScheduleIds,
		sortSchedulesByTitle,
		schedulesPage,
	])

	// Public schedules from the current page (excluding any that are assigned)
	const publicSchedules = useMemo(() => {
		return (publicSchedulesResponse?.data || [])
			.filter((schedule: Schedule) => !assignedScheduleIds.has(schedule.id))
			.sort(sortSchedulesByTitle)
	}, [publicSchedulesResponse?.data, assignedScheduleIds, sortSchedulesByTitle])

	const combinedSchedules = useMemo(() => {
		return [
			...assignedSchedules,
			...unassignedPrivateSchedules,
			...publicSchedules,
		]
	}, [assignedSchedules, unassignedPrivateSchedules, publicSchedules])

	const isLoadingSchedules =
		isLoadingPrivateSchedules || isLoadingPublicSchedules

	// Fetch campaigns with pagination and search
	const { data: campaignsResponse, isLoading: isLoadingCampaigns } =
		usePaginateCampaigns({
			config: useAccountConfig({
				params: {
					page: campaignsPage,
					per_page: campaignsPageSize,
					query: debouncedSearchQuery || undefined,
					include: 'account,adverts',
				},
			}),
			options: {
				enabled: visible && activeTab === 'campaigns',
			},
		})

	const campaigns = campaignsResponse?.data || []
	const campaignsPaginationMeta = campaignsResponse?.meta?.pagination

	// Fetch playlists with pagination and search
	const { data: playlistsResponse, isLoading: isLoadingPlaylists } =
		usePlaylists({
			config: useAccountConfig({
				params: {
					page: playlistsPage,
					per_page: playlistsPageSize,
					query: debouncedSearchQuery || undefined,
				},
			}),
			options: {
				enabled: visible && showPlaylistsTab,
			},
		})

	const availablePlaylists = playlistsResponse?.data || []
	const playlistsPaginationMeta = playlistsResponse?.meta?.pagination

	// Fetch sample for the selected playlist
	const { data: sampleSongs, isLoading: isLoadingSample } = usePlaylistSample({
		playlistId: playlistIdForSample,
		config: useAccountConfig({}),
		options: {
			enabled: visible && showPlaylistsTab && playlistIdForSample !== null,
		},
	})

	// Set current song when sample data is loaded
	useEffect(() => {
		if (sampleSongs && sampleSongs.length > 0) {
			setCurrentSong(sampleSongs[0])
		}
	}, [sampleSongs])

	// Handle play/stop button click for schedule covers
	const handlePlaylistPlayClick = (
		scheduleId: number,
		playlist: Playlist,
		e: MouseEvent<HTMLButtonElement>
	) => {
		e.stopPropagation()
		if (
			playingScheduleId === scheduleId &&
			playlistIdForSample === playlist.id
		) {
			// Stop playing
			handlePlayerStop()
		} else {
			// Start playing this playlist sample
			setPlayingScheduleId(scheduleId)
			setPlaylistIdForSample(playlist.id)
			setPlayingPlaylist(playlist)
			setCurrentSong(null)
		}
	}

	const handlePlaylistRowPlayClick = (
		playlist: Playlist,
		e: MouseEvent<HTMLButtonElement>
	) => {
		e.stopPropagation()
		if (playingScheduleId === null && playlistIdForSample === playlist.id) {
			handlePlayerStop()
		} else {
			setPlayingScheduleId(null)
			setPlaylistIdForSample(playlist.id)
			setPlayingPlaylist(playlist)
			setCurrentSong(null)
		}
	}

	// Handle player pause/stop
	const handlePlayerStop = () => {
		setPlayingScheduleId(null)
		setPlayingPlaylist(null)
		setPlaylistIdForSample(null)
		setCurrentSong(null)
	}

	const getSchedulePlaylists = (schedule: Schedule) => {
		const playlistsMap = new Map<number, Playlist>()
		schedule.timeslots?.forEach((timeslot) => {
			timeslot.timeslotContents?.forEach((content) => {
				content.playlists?.forEach((playlist) => {
					playlistsMap.set(playlist.id, playlist)
				})
			})
		})
		return Array.from(playlistsMap.values())
	}

	// Get the currently playing schedule data
	const playingSchedule = combinedSchedules.find(
		(s) => s.id === playingScheduleId
	)

	// Fetch existing group schedules
	const { data: existingGroupSchedules } = useGetGroupSchedules({
		groupId: group?.id,
		config: useAccountConfig({}),
		options: {
			enabled: visible && !!group?.id,
		},
	})

	// Pre-populate selections with existing schedules and campaigns
	useEffect(() => {
		if (!visible || !group) {
			return
		}

		// In draft mode, always use group.schedules, group.campaigns, and group.playlists
		// which contain the draft state
		if (isDraftMode) {
			// Set existing schedules from draft state
			const groupSchedules = group.schedules || []
			const scheduleIds = groupSchedules.map((s) => s.id)
			setSelectedScheduleIds(scheduleIds)
			setSelectedSchedules(groupSchedules)

			// Set existing campaigns from draft state
			const groupCampaigns = group.campaigns || []
			const campaignIds = groupCampaigns.map((c) => c.id)
			setSelectedCampaignIds(campaignIds)
			setSelectedCampaigns(groupCampaigns)

			// Set existing playlists from draft state
			const groupPlaylists = group.playlists || []
			const playlistIds = groupPlaylists.map((p) => p.id)
			setSelectedPlaylistIds(playlistIds)
			setSelectedPlaylists(groupPlaylists)
		} else {
			// In non-draft mode, use the fetched data or group data
			// Set existing schedules
			if (existingGroupSchedules && Array.isArray(existingGroupSchedules)) {
				const scheduleIds = existingGroupSchedules.map((s) => s.id)
				setSelectedScheduleIds(scheduleIds)
				setSelectedSchedules(existingGroupSchedules)
			} else if (group.schedules && Array.isArray(group.schedules)) {
				const scheduleIds = group.schedules.map((s) => s.id)
				setSelectedScheduleIds(scheduleIds)
				setSelectedSchedules(group.schedules)
			}

			// Set existing campaigns
			if (group.campaigns && Array.isArray(group.campaigns)) {
				const campaignIds = group.campaigns.map((c) => c.id)
				setSelectedCampaignIds(campaignIds)
				setSelectedCampaigns(group.campaigns)
			}

			// Set existing playlists
			if (group.playlists && Array.isArray(group.playlists)) {
				const playlistIds = group.playlists.map((p) => p.id)
				setSelectedPlaylistIds(playlistIds)
				setSelectedPlaylists(group.playlists)
			}
		}
	}, [
		visible,
		group?.schedules,
		group?.campaigns,
		group?.playlists,
		existingGroupSchedules,
		isDraftMode,
	])

	// Mutations
	const { mutateAsync: updateGroupSchedules } = useUpdateGroupSchedules({
		config: useAccountConfig({}),
		options: {
			onSuccess: () => {
				queryClient.invalidateQueries({ queryKey: ['group-schedules'] })
				queryClient.invalidateQueries({ queryKey: ['group'] })
			},
		},
	})

	const { mutateAsync: updateGroupCampaigns } = useUpdateGroupCampaigns({
		config: useAccountConfig({}),
		options: {
			onSuccess: () => {
				queryClient.invalidateQueries({ queryKey: ['group'] })
			},
		},
	})

	// Handle pagination changes for schedules
	const onSchedulesPageChange = useCallback((newPage: number) => {
		setSchedulesPage(newPage)
	}, [])

	// Handle pagination changes for campaigns
	const onCampaignsPageChange = useCallback((newPage: number) => {
		setCampaignsPage(newPage)
	}, [])

	// Handle pagination changes for playlists
	const onPlaylistsPageChange = useCallback((newPage: number) => {
		setPlaylistsPage(newPage)
	}, [])

	const handleTogglePlaylist = (playlist: Playlist) => {
		const playlistId = playlist.id
		const isSelected = selectedPlaylistIds.includes(playlistId)
		if (
			!isSelected &&
			selectedPlaylistIds.length >= MAX_PLAYLISTS_PER_GROUP_OR_ZONE
		) {
			showNotification({
				type: 'error',
				message: t(
					'entities.group.create.step2.limits.max_playlists_exceeded',
					{
						max: MAX_PLAYLISTS_PER_GROUP_OR_ZONE,
					}
				),
			})
			return
		}

		if (selectedPlaylistIds.includes(playlistId)) {
			setSelectedPlaylistIds((prev) => prev.filter((id) => id !== playlistId))
			setSelectedPlaylists((prev) => prev.filter((p) => p.id !== playlistId))
		} else {
			setSelectedPlaylistIds((prev) => [...prev, playlistId])
			setSelectedPlaylists((prev) => [...prev, playlist])
		}
	}

	const handleToggleSchedule = (schedule: Schedule) => {
		const scheduleId = schedule.id
		const isSelected = selectedScheduleIds.includes(scheduleId)
		if (
			!isSelected &&
			selectedScheduleIds.length >= MAX_SCHEDULES_PER_GROUP_OR_ZONE
		) {
			showNotification({
				type: 'error',
				message: t(
					'entities.group.create.step2.limits.max_schedules_exceeded',
					{
						max: MAX_SCHEDULES_PER_GROUP_OR_ZONE,
					}
				),
			})
			return
		}

		if (selectedScheduleIds.includes(scheduleId)) {
			setSelectedScheduleIds((prev) => prev.filter((id) => id !== scheduleId))
			setSelectedSchedules((prev) => prev.filter((s) => s.id !== scheduleId))
		} else {
			setSelectedScheduleIds((prev) => [...prev, scheduleId])
			setSelectedSchedules((prev) => [...prev, schedule])
		}
	}

	const handleToggleCampaign = (campaign: Campaign) => {
		const campaignId = campaign.id
		if (selectedCampaignIds.includes(campaignId)) {
			setSelectedCampaignIds((prev) => prev.filter((id) => id !== campaignId))
			setSelectedCampaigns((prev) => prev.filter((c) => c.id !== campaignId))
		} else {
			setSelectedCampaignIds((prev) => [...prev, campaignId])
			setSelectedCampaigns((prev) => [...prev, campaign])
		}
	}

	const handleConfirm = async () => {
		if (!group) return

		// In create mode, just return the selected IDs
		if (isCreateMode && onConfirmCreate) {
			onConfirmCreate(
				selectedScheduleIds,
				selectedCampaignIds,
				selectedPlaylistIds
			)
			return
		}

		// In draft mode, call the draft callback with full objects
		if (isDraftMode && onConfirmDraft) {
			onConfirmDraft(selectedSchedules, selectedCampaigns, selectedPlaylists)
			setSearchQuery('')
			setSchedulesPage(DEFAULT_PAGE)
			setCampaignsPage(DEFAULT_PAGE)
			handlePlayerStop()
			onClose()
			return
		}

		// Edit mode: Update the existing group
		setIsAssigning(true)

		try {
			const promises = []

			// Update schedules if any are selected and different from existing
			const existingScheduleIds = (group.schedules || []).map((s) => s.id)
			const hasScheduleChanges =
				JSON.stringify([...selectedScheduleIds].sort()) !==
				JSON.stringify([...existingScheduleIds].sort())

			if (hasScheduleChanges) {
				promises.push(
					updateGroupSchedules({
						groupId: group.id,
						data: { schedules: selectedScheduleIds },
					})
				)
			}

			// Update campaigns if any are selected and different from existing
			const existingCampaignIds = (group.campaigns || []).map((c) => c.id)
			const hasCampaignChanges =
				JSON.stringify([...selectedCampaignIds].sort()) !==
				JSON.stringify([...existingCampaignIds].sort())

			if (hasCampaignChanges) {
				promises.push(
					updateGroupCampaigns({
						groupId: group.id,
						data: { announcements: selectedCampaignIds },
					})
				)
			}

			if (promises.length > 0) {
				await Promise.all(promises)

				// Calculate changes for notification
				const addedSchedules = selectedScheduleIds.filter(
					(id) => !existingScheduleIds.includes(id)
				).length
				const removedSchedules = existingScheduleIds.filter(
					(id) => !selectedScheduleIds.includes(id)
				).length
				const addedCampaigns = selectedCampaignIds.filter(
					(id) => !existingCampaignIds.includes(id)
				).length
				const removedCampaigns = existingCampaignIds.filter(
					(id) => !selectedCampaignIds.includes(id)
				).length

				const changes = []
				if (addedSchedules > 0)
					changes.push(
						`Added ${addedSchedules} schedule${addedSchedules > 1 ? 's' : ''}`
					)
				if (removedSchedules > 0)
					changes.push(
						`Removed ${removedSchedules} schedule${removedSchedules > 1 ? 's' : ''}`
					)
				if (addedCampaigns > 0)
					changes.push(
						`Added ${addedCampaigns} campaign${addedCampaigns > 1 ? 's' : ''}`
					)
				if (removedCampaigns > 0)
					changes.push(
						`Removed ${removedCampaigns} campaign${removedCampaigns > 1 ? 's' : ''}`
					)

				showNotification({
					type: 'success',
					message: 'Content updated successfully',
					description: changes.join(', '),
				})
			}

			// Reset state and close modal
			setSearchQuery('')
			handlePlayerStop()
			onClose()
		} catch (error) {
			console.error('Error updating group content:', error)
			showNotification({
				type: 'error',
				message: 'Update failed',
				description: 'Failed to update group content. Please try again.',
			})
		} finally {
			setIsAssigning(false)
		}
	}

	const handleCancel = () => {
		setSearchQuery('')
		setSchedulesPage(DEFAULT_PAGE)
		setCampaignsPage(DEFAULT_PAGE)
		setPlaylistsPage(DEFAULT_PAGE)
		handlePlayerStop()
		onClose()
	}

	const handleClose = (open: boolean) => {
		if (!open && !(isCreateMode ? isCreating : isAssigning)) {
			handleCancel()
		}
	}

	return (
		<>
			<Dialog open={visible} onOpenChange={handleClose}>
				<DialogContent
					className="max-w-full sm:max-w-3xl p-0 gap-0 flex flex-col max-h-[100dvh] sm:max-h-[90vh] overflow-hidden rounded-none sm:rounded-lg"
					showCloseButton={false}
				>
					<DialogHeader className="px-4 sm:px-6 pt-4 sm:pt-5 pb-2 sm:pb-3 flex-none">
						<DialogTitle className="text-xl">
							{isCreateMode
								? t('entities.group.create.step2.title')
								: t('entities.group.edit.add_content_title')}
						</DialogTitle>
					</DialogHeader>

					<Separator />

					<div className="px-4 sm:px-6 py-2 sm:py-3 flex flex-col flex-1 min-h-0 overflow-hidden">
						{/* Tabs */}
						<Tabs
							value={activeTab}
							onValueChange={(value) => setActiveTab(value as ContentTab)}
							className="flex-1 flex flex-col min-h-0"
						>
							<TabsList className="flex-none mb-3">
								{showPlaylistsTab && (
									<TabsTrigger value="playlists">
										{t('entities.group.create.step2.tabs.playlists')}
										<TabsBadge>{selectedPlaylistIds.length}</TabsBadge>
									</TabsTrigger>
								)}
								<TabsTrigger value="schedules">
									{t('entities.group.create.step2.tabs.schedules')}
									<TabsBadge>{selectedScheduleIds.length}</TabsBadge>
								</TabsTrigger>
								<TabsTrigger value="campaigns">
									{t('entities.group.create.step2.tabs.campaigns')}
									<TabsBadge>{selectedCampaignIds.length}</TabsBadge>
								</TabsTrigger>
							</TabsList>

							{showPlaylistsTab && (
								<TabsContent
									value="playlists"
									className="mt-0 flex-1 flex flex-col min-h-0"
								>
									{/* Search */}
									<div className="relative flex-none mb-3">
										<Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
										<Input
											placeholder={t('actions.search')}
											value={searchQuery}
											onChange={(e) => setSearchQuery(e.target.value)}
											className="pl-10"
										/>
									</div>

									{/* Playlists List */}
									<div>
										<ScrollArea className="h-[calc(100dvh-340px)] sm:h-[calc(90vh-340px)] border rounded-md">
											<div className="h-full p-2">
												{isLoadingPlaylists ? (
													<div className="flex items-center justify-center h-full text-muted-foreground text-sm">
														{t('entities.playlist.assign.loading')}
													</div>
												) : availablePlaylists.length === 0 ? (
													<div className="flex items-center justify-center h-full text-muted-foreground text-sm">
														{t('entities.playlist.assign.no_groups_found')}
													</div>
												) : (
													<div className="pb-10">
														{availablePlaylists.map((playlist: Playlist) => (
															<PlaylistRow
																key={`playlist-${playlist.id}`}
																playlist={playlist}
																isSelected={selectedPlaylistIds.includes(
																	playlist.id
																)}
																onSelectionChange={() =>
																	handleTogglePlaylist(playlist)
																}
																isPlaying={playlistIdForSample === playlist.id}
																isLoadingPlayback={
																	isLoadingSample &&
																	playlistIdForSample === playlist.id
																}
																isPlaybackReady={Boolean(currentSong)}
																onPlayClick={(event) =>
																	handlePlaylistRowPlayClick(playlist, event)
																}
															/>
														))}
													</div>
												)}
											</div>
										</ScrollArea>
									</div>

									<ModalPaginator
										currentPage={playlistsPage}
										totalPages={playlistsPaginationMeta?.total_pages ?? 1}
										onPageChange={onPlaylistsPageChange}
									/>
								</TabsContent>
							)}

							<TabsContent
								value="schedules"
								className="mt-0 flex-1 flex flex-col min-h-0"
							>
								{/* Search */}
								<div className="relative flex-none mb-3">
									<Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
									<Input
										placeholder={t(
											'entities.group.create.step2.schedules.search_placeholder'
										)}
										value={searchQuery}
										onChange={(e) => setSearchQuery(e.target.value)}
										className="pl-10"
									/>
								</div>

								{/* Schedules List */}
								<div>
									<ScrollArea className="h-[calc(100dvh-340px)] sm:h-[calc(90vh-340px)] border rounded-md">
										<div className="h-full pb-6">
											{isLoadingSchedules && combinedSchedules.length === 0 ? (
												<div className="flex items-center justify-center h-full text-muted-foreground text-sm pt-3">
													{t('entities.group.create.step2.schedules.loading')}
												</div>
											) : combinedSchedules.length === 0 ? (
												<div className="flex items-center justify-center h-full text-muted-foreground text-sm pt-3">
													{t(
														'entities.group.create.step2.schedules.no_results'
													)}
												</div>
											) : (
												<div className="divide-y pb-10">
													{combinedSchedules.map((schedule: Schedule) => (
														<ScheduleRow
															key={`schedule-${schedule.id}`}
															schedule={schedule}
															isSelected={selectedScheduleIds.includes(
																schedule.id
															)}
															onSelectionChange={() =>
																handleToggleSchedule(schedule)
															}
															playlists={getSchedulePlaylists(schedule)}
															playingScheduleId={playingScheduleId}
															playlistIdForSample={playlistIdForSample}
															isLoadingSample={isLoadingSample}
															currentSong={currentSong}
															onPlaylistPlayClick={handlePlaylistPlayClick}
														/>
													))}
												</div>
											)}
										</div>
									</ScrollArea>
								</div>

								<ModalPaginator
									currentPage={schedulesPage}
									totalPages={schedulesPaginationMeta?.total_pages ?? 1}
									onPageChange={onSchedulesPageChange}
								/>
							</TabsContent>

							<TabsContent
								value="campaigns"
								className="mt-0 flex-1 flex flex-col min-h-0"
							>
								{/* Search */}
								<div className="relative flex-none mb-3">
									<Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
									<Input
										placeholder={t(
											'entities.group.create.step2.campaigns.search_placeholder'
										)}
										value={searchQuery}
										onChange={(e) => setSearchQuery(e.target.value)}
										className="pl-10"
									/>
								</div>

								{/* Campaigns List */}
								<div>
									<ScrollArea className="h-[calc(100dvh-340px)] sm:h-[calc(90vh-340px)] border rounded-md">
										<div className="h-full p-2">
											{isLoadingCampaigns ? (
												<div className="flex items-center justify-center h-full text-muted-foreground text-sm">
													{t('entities.group.create.step2.campaigns.loading')}
												</div>
											) : campaigns.length === 0 ? (
												<div className="flex items-center justify-center h-full text-muted-foreground text-sm">
													{t(
														'entities.group.create.step2.campaigns.no_results'
													)}
												</div>
											) : (
												<div className="divide-y pb-10">
													{campaigns.map((campaign: Campaign) => {
														const advertCount = campaign.adverts?.length || 0
														return (
															<div
																key={`campaign-${campaign.id}`}
																className="flex items-center justify-between px-4 py-4 hover:bg-accent/50 cursor-pointer"
																onClick={() => handleToggleCampaign(campaign)}
															>
																<div className="flex-1 pr-4 flex flex-col justify-center">
																	<p className="text-sm font-medium mb-1">
																		{campaign.name}
																	</p>
																	{advertCount > 0 && (
																		<p className="text-xs text-muted-foreground">
																			{t(
																				'entities.group.create.form.mixes.adverts',
																				{ count: advertCount }
																			)}
																		</p>
																	)}
																</div>
																<Checkbox
																	checked={selectedCampaignIds.includes(
																		campaign.id
																	)}
																	onCheckedChange={() =>
																		handleToggleCampaign(campaign)
																	}
																	onClick={(e) => e.stopPropagation()}
																/>
															</div>
														)
													})}
												</div>
											)}
										</div>
									</ScrollArea>
								</div>

								<ModalPaginator
									currentPage={campaignsPage}
									totalPages={campaignsPaginationMeta?.total_pages ?? 1}
									onPageChange={onCampaignsPageChange}
								/>
							</TabsContent>
						</Tabs>
					</div>

					<Separator />

					{/* Footer Buttons */}
					<DialogFooter className="px-4 sm:px-6 py-3 sm:py-4 flex-none">
						<Button
							type="button"
							variant="outline"
							onClick={handleCancel}
							className="h-9 rounded-full px-6"
							disabled={isCreateMode ? isCreating : isAssigning}
						>
							{t('actions.cancel')}
						</Button>
						<Button
							type="button"
							onClick={handleConfirm}
							disabled={isCreateMode ? isCreating : isAssigning}
							className="h-9 rounded-full px-6"
						>
							{isCreateMode
								? isCreating
									? t('actions.creating')
									: t('entities.group.create.step2.create')
								: isAssigning
									? t('actions.saving')
									: t('actions.confirm')}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			{/* Hidden audio player for schedule and playlist cover previews */}
			{currentSong && playingPlaylist && (
				<div className="hidden">
					<Player
						src={currentSong.path}
						cover={playingPlaylist.getSmallestThumbnail()}
						title={currentSong.metadata.title ?? t('common.loading')}
						radioPrevText={t('entities.schedule.playlist_selector.preview')}
						radioTitle={playingSchedule?.title || playingPlaylist.title}
						metadata={[
							currentSong.metadata.artist || '',
							currentSong.metadata.album || '',
						].filter(Boolean)}
						showSkipControls={false}
						showJumpControls={false}
						autoPlay={true}
						onPause={handlePlayerStop}
						hideVolume={true}
						cssVars={{
							border: 'none',
							backgroundColor: 'transparent',
						}}
					/>
				</div>
			)}
		</>
	)
}
